/* eslint-disable @typescript-eslint/no-explicit-any, @typescript-eslint/no-non-null-assertion */
import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import ReactFlow, {
  Background,
  Controls,
  MiniMap,
  useNodesState,
  useEdgesState,
  MarkerType,
  type Node,
  type Edge,
} from 'reactflow';
import 'reactflow/dist/style.css';
import {
  Activity,
  X,
  Upload,
  FileText,
  ChevronLeft,
  Filter,
  Search,
  CheckCircle2,
  XCircle,
  Zap,
  Ban,
} from 'lucide-react';

import type {
  FlowStream,
  TrafficPath,
  TrafficHop,
  DiscoveryIndex,
  StreamFilter,
  TrafficAnalyzerView,
} from './types';
import { formatBytes, formatNumber } from './parser';
import { buildDiscoveryIndex } from './discovery-index';
import { analyzeTrafficPath } from './path-analyzer';
import { trafficNodeTypes, DirectionBadge, ConnectionTypeBadge, type TrafficNodeData } from './TrafficNodes';
import { useFlowParser, type ParseStats } from './useFlowParser';

// =============================================================================
// MAIN TRAFFIC ANALYZER COMPONENT
// =============================================================================

interface TrafficAnalyzerProps {
  discoveryData: any;
  onClose: () => void;
}

export const TrafficAnalyzer: React.FC<TrafficAnalyzerProps> = ({
  discoveryData,
  onClose,
}) => {
  // View state
  const [view, setView] = useState<TrafficAnalyzerView>('upload');
  const [visualizationLoading, setVisualizationLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Flow parser hook (Web Worker based)
  const {
    parseFlowLogs,
    cancelParsing,
    streams: parsedStreams,
    progress,
    stats,
    error: parserError,
    isProcessing
  } = useFlowParser();

  // Data state
  const [streams, setStreams] = useState<FlowStream[]>([]);
  const [selectedStreams, setSelectedStreams] = useState<Set<string>>(new Set());
  const [analysisResults, setAnalysisResults] = useState<TrafficPath[]>([]);

  // Filter state
  const [filter, setFilter] = useState<StreamFilter>({
    protocol: 'all',
    action: 'all',
    direction: 'all',
    searchIp: '',
  });

  // ReactFlow state
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);

  // Refs
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Build discovery index once
  const discoveryIndex = useMemo<DiscoveryIndex>(
    () => buildDiscoveryIndex(discoveryData),
    [discoveryData]
  );

  // Extract VPC CIDRs from discovery data for direction detection
  const vpcCidrs = useMemo(() => {
    const cidrs: string[] = [];
    if (discoveryData?.vpcs) {
      for (const vpc of Object.values(discoveryData.vpcs) as any[]) {
        if (vpc.info?.cidr_block) {
          cidrs.push(vpc.info.cidr_block);
        }
        // Also check for cidr_block directly on vpc
        if (vpc.cidr_block) {
          cidrs.push(vpc.cidr_block);
        }
      }
    }
    return cidrs;
  }, [discoveryData]);

  // Update streams when parsing completes
  useEffect(() => {
    if (parsedStreams.length > 0 && progress.phase === 'complete') {
      console.log('[TrafficAnalyzer] Parsing complete! Setting', parsedStreams.length, 'streams');
      const start = performance.now();
      setStreams(parsedStreams);
      console.log('[TrafficAnalyzer] setStreams took', (performance.now() - start).toFixed(2), 'ms');
      setView('list');
      console.log('[TrafficAnalyzer] View changed to list');
    }
  }, [parsedStreams, progress.phase]);

  // Sync parser error to component error
  useEffect(() => {
    if (parserError) {
      setError(parserError);
    }
  }, [parserError]);

  // ==========================================================================
  // FILE UPLOAD HANDLING
  // ==========================================================================

  const handleFileSelect = useCallback(async (file: File) => {
    setError(null);
    try {
      const text = await file.text();
      parseFlowLogs(text, vpcCidrs);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to read file');
    }
  }, [parseFlowLogs, vpcCidrs]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const file = e.dataTransfer.files[0];
    if (file) {
      handleFileSelect(file);
    }
  }, [handleFileSelect]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  // ==========================================================================
  // STREAM SELECTION
  // ==========================================================================

  const handleStreamSelect = useCallback((streamId: string, selected: boolean) => {
    setSelectedStreams(prev => {
      const next = new Set(prev);
      if (selected) {
        next.add(streamId);
      } else {
        next.delete(streamId);
      }
      return next;
    });
  }, []);

  const handleSelectAll = useCallback(() => {
    const allIds = filteredStreams.map(s => s.id);
    setSelectedStreams(new Set(allIds));
  }, []);

  const handleDeselectAll = useCallback(() => {
    setSelectedStreams(new Set());
  }, []);

  // ==========================================================================
  // FILTERING
  // ==========================================================================

  const filteredStreams = useMemo(() => {
    return streams.filter(stream => {
      // Protocol filter
      if (filter.protocol !== 'all') {
        const proto = filter.protocol === 'tcp' ? 6 : filter.protocol === 'udp' ? 17 : 1;
        if (stream.protocol !== proto) return false;
      }

      // Action filter
      if (filter.action !== 'all' && stream.action !== filter.action) return false;

      // Direction filter
      if (filter.direction !== 'all' && stream.direction !== filter.direction) return false;

      // IP search
      if (filter.searchIp) {
        const search = filter.searchIp.toLowerCase();
        if (!stream.srcAddr.includes(search) && !stream.dstAddr.includes(search)) {
          return false;
        }
      }

      return true;
    });
  }, [streams, filter]);

  // ==========================================================================
  // VISUALIZATION
  // ==========================================================================

  const handleVisualize = useCallback(() => {
    setVisualizationLoading(true);

    // Run analysis in next tick to allow loading state to render
    setTimeout(() => {
      try {
        const selectedStreamsList = streams.filter(s => selectedStreams.has(s.id));

        const results: TrafficPath[] = selectedStreamsList.map(stream =>
          analyzeTrafficPath(stream, discoveryIndex)
        );

        setAnalysisResults(results);

        // Generate ReactFlow layout
        const { nodes: newNodes, edges: newEdges } = generateFlowLayout(results);
        setNodes(newNodes);
        setEdges(newEdges);

        setView('visualization');
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Analysis failed');
      } finally {
        setVisualizationLoading(false);
      }
    }, 50);
  }, [streams, selectedStreams, discoveryIndex, setNodes, setEdges]);

  // ==========================================================================
  // LAYOUT GENERATION
  // ==========================================================================

  function generateFlowLayout(results: TrafficPath[]): { nodes: Node[]; edges: Edge[] } {
    const xSpacing = 280;
    const ySpacing = 120;
    
    // Step 1: Collect all paths as sequences of hop IDs
    const pathSequences: Array<{
      pathIndex: number;
      hops: TrafficHop[];
      hopIds: string[];
    }> = results.map((result, pathIndex) => {
      const hops = result.stream.direction === 'inbound'
        ? result.inboundPath
        : result.outboundPath;
      return {
        pathIndex,
        hops,
        hopIds: hops.map(h => h.id),
      };
    });
    
    // Step 2: Group nodes by column (hop position) and identify unique nodes per column
    // A node is unique by its ID - same ID means same resource
    const columnNodes = new Map<number, Map<string, { hop: TrafficHop; paths: number[] }>>();
    
    // Find the maximum path length
    const maxHops = Math.max(...pathSequences.map(p => p.hops.length));
    
    // Initialize columns
    for (let col = 0; col < maxHops; col++) {
      columnNodes.set(col, new Map());
    }
    
    // Populate columns with unique nodes
    pathSequences.forEach(({ pathIndex, hops }) => {
      hops.forEach((hop, hopIndex) => {
        const column = columnNodes.get(hopIndex)!;
        if (column.has(hop.id)) {
          // Node already exists, add this path to its list
          column.get(hop.id)!.paths.push(pathIndex);
        } else {
          // New unique node for this column
          column.set(hop.id, { hop, paths: [pathIndex] });
        }
      });
    });
    
    // Step 3: Calculate Y positions for nodes
    // Nodes that are shared by multiple paths get centered
    // Nodes that are unique to a path get offset vertically
    const nodePositions = new Map<string, { x: number; y: number }>();
    const pathYOffsets = new Map<number, number>(); // Track Y offset per path for diverged nodes
    
    // Initialize path Y offsets
    pathSequences.forEach((_, idx) => pathYOffsets.set(idx, idx * ySpacing));
    
    // Process each column
    for (let col = 0; col < maxHops; col++) {
      const column = columnNodes.get(col)!;
      const nodesInColumn = Array.from(column.entries());
      
      // Sort nodes by number of paths (shared nodes first)
      nodesInColumn.sort((a, b) => b[1].paths.length - a[1].paths.length);
      
      let currentY = 0;
      nodesInColumn.forEach(([nodeId, { paths }]) => {
        if (paths.length > 1) {
          // Shared node - center it among all the paths that use it
          const avgY = paths.reduce((sum, p) => sum + (pathYOffsets.get(p) || 0), 0) / paths.length;
          nodePositions.set(nodeId, { x: col * xSpacing, y: avgY });
          // Update path offsets to converge at this node
          paths.forEach(p => pathYOffsets.set(p, avgY));
        } else {
          // Unique node - use the path's current Y offset
          const pathY = pathYOffsets.get(paths[0]) || currentY;
          nodePositions.set(nodeId, { x: col * xSpacing, y: pathY });
          currentY += ySpacing;
        }
      });
    }
    
    // Step 4: Create ReactFlow nodes
    const allNodes: Node[] = [];
    const addedNodeIds = new Set<string>();
    
    pathSequences.forEach(({ hops }) => {
      hops.forEach((hop, hopIndex) => {
        if (addedNodeIds.has(hop.id)) return; // Skip if already added
        addedNodeIds.add(hop.id);
        
        const position = nodePositions.get(hop.id) || { x: hopIndex * xSpacing, y: 0 };
        const column = columnNodes.get(hopIndex)!;
        const nodeInfo = column.get(hop.id);
        const pathCount = nodeInfo?.paths.length || 1;
        
        const nodeData: TrafficNodeData = {
          ...hop,
          label: hop.name,
          isFirst: hopIndex === 0,
          isLast: hopIndex === hops.length - 1,
        };
        
        allNodes.push({
          id: hop.id,
          type: 'trafficHop',
          position,
          data: {
            ...nodeData,
            // Add info about how many paths share this node
            sharedByPaths: pathCount > 1 ? pathCount : undefined,
          },
        });
      });
    });
    
    // Step 5: Create edges
    const allEdges: Edge[] = [];
    const addedEdgeIds = new Set<string>();
    
    pathSequences.forEach(({ hops }) => {
      hops.forEach((hop, hopIndex) => {
        if (hopIndex < hops.length - 1) {
          const nextHop = hops[hopIndex + 1];
          const edgeId = `${hop.id}->${nextHop.id}`;
          
          if (addedEdgeIds.has(edgeId)) return; // Skip duplicate edges
          addedEdgeIds.add(edgeId);
          
          // Determine edge color based on status
          const isAllowed = hop.status !== 'denied';
          const color = isAllowed ? '#10b981' : '#ef4444';
          
          allEdges.push({
            id: edgeId,
            source: hop.id,
            target: nextHop.id,
            type: 'smoothstep',
            animated: true,
            style: {
              stroke: color,
              strokeWidth: 2,
            },
            markerEnd: {
              type: MarkerType.ArrowClosed,
              color,
            },
          });
        }
      });
    });
    
    return { nodes: allNodes, edges: allEdges };
  }

  // ==========================================================================
  // RENDER
  // ==========================================================================

  return (
    <div className="fixed inset-0 z-[9999] bg-slate-900/90 backdrop-blur-sm flex flex-col">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between flex-shrink-0">
        <div className="flex items-center gap-4">
          {view !== 'upload' && !isProcessing && (
            <button
              onClick={() => setView(view === 'visualization' ? 'list' : 'upload')}
              className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
            >
              <ChevronLeft className="w-5 h-5 text-slate-600" />
            </button>
          )}
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-xl flex items-center justify-center">
              <Activity className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-slate-800">Traffic Analyzer</h1>
              <p className="text-sm text-slate-500">
                {view === 'upload' && !isProcessing && 'Upload VPC Flow Logs to analyze traffic paths'}
                {isProcessing && `Processing: ${progress.phase === 'parsing' ? 'Parsing' : 'Aggregating'}...`}
                {view === 'list' && !isProcessing && `${streams.length} streams parsed`}
                {view === 'visualization' && `Visualizing ${analysisResults.length} traffic path${analysisResults.length !== 1 ? 's' : ''}`}
              </p>
            </div>
          </div>
        </div>
        <button
          onClick={onClose}
          className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
        >
          <X className="w-5 h-5 text-slate-600" />
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-hidden relative">
        {visualizationLoading && <LoadingOverlay message="Analyzing traffic paths..." />}
        {error && <ErrorBanner error={error} onDismiss={() => setError(null)} />}

        {/* Upload View or Processing View */}
        {(view === 'upload' || isProcessing) && (
          <UploadView
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onFileSelect={handleFileSelect}
            fileInputRef={fileInputRef}
            isProcessing={isProcessing}
            progress={progress}
            onCancel={cancelParsing}
          />
        )}

        {view === 'list' && !isProcessing && (
          <StreamListView
            streams={filteredStreams}
            selectedStreams={selectedStreams}
            filter={filter}
            stats={stats}
            onFilterChange={setFilter}
            onStreamSelect={handleStreamSelect}
            onSelectAll={handleSelectAll}
            onDeselectAll={handleDeselectAll}
            onVisualize={handleVisualize}
          />
        )}

        {view === 'visualization' && !isProcessing && (
          <VisualizationView
            results={analysisResults}
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
          />
        )}
      </div>
    </div>
  );
};

// =============================================================================
// UPLOAD VIEW (with Progress)
// =============================================================================

interface UploadViewProps {
  onDrop: (e: React.DragEvent) => void;
  onDragOver: (e: React.DragEvent) => void;
  onFileSelect: (file: File) => void;
  fileInputRef: React.RefObject<HTMLInputElement>;
  isProcessing: boolean;
  progress: {
    progress: number;
    phase: string;
    currentLine?: number;
    totalLines?: number;
  };
  onCancel: () => void;
}

const UploadView: React.FC<UploadViewProps> = ({
  onDrop,
  onDragOver,
  onFileSelect,
  fileInputRef,
  isProcessing,
  progress,
  onCancel,
}) => {
  const [isDragging, setIsDragging] = useState(false);

  // Show processing state
  if (isProcessing) {
    return (
      <div className="h-full flex items-center justify-center p-8">
        <div className="w-full max-w-md bg-white rounded-2xl p-8 shadow-xl border border-slate-200">
          <div className="flex flex-col items-center">
            {/* Circular Progress */}
            <div className="relative w-24 h-24 mb-6">
              <svg className="w-24 h-24 transform -rotate-90">
                <circle
                  cx="48"
                  cy="48"
                  r="40"
                  stroke="#e5e7eb"
                  strokeWidth="6"
                  fill="none"
                />
                <circle
                  cx="48"
                  cy="48"
                  r="40"
                  stroke="#0891b2"
                  strokeWidth="6"
                  fill="none"
                  strokeLinecap="round"
                  strokeDasharray={251.33}
                  strokeDashoffset={251.33 * (1 - progress.progress / 100)}
                  className="transition-all duration-300"
                />
              </svg>
              <span className="absolute inset-0 flex items-center justify-center text-xl font-bold text-cyan-600">
                {Math.round(progress.progress)}%
              </span>
            </div>
            
            {/* Phase info */}
            <h3 className="text-lg font-semibold text-slate-800 mb-1">
              {progress.phase === 'parsing' && 'Parsing Flow Logs'}
              {progress.phase === 'aggregating' && 'Aggregating Streams'}
            </h3>
            
            {progress.currentLine !== undefined && progress.totalLines !== undefined && (
              <p className="text-sm text-slate-500 mb-4">
                {formatNumber(progress.currentLine)} / {formatNumber(progress.totalLines)} lines
              </p>
            )}
            
            <p className="text-xs text-slate-400 mb-6 text-center">
              Processing in background thread - UI stays responsive
            </p>
            
            {/* Cancel button */}
            <button
              onClick={onCancel}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
            >
              <Ban className="w-4 h-4" />
              Cancel
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Upload dropzone
  return (
    <div className="h-full flex items-center justify-center p-8">
      <div
        className={`
          w-full max-w-2xl border-2 border-dashed rounded-2xl p-12 text-center
          transition-all duration-200 cursor-pointer
          ${isDragging
            ? 'border-cyan-500 bg-cyan-50'
            : 'border-slate-300 bg-white hover:border-cyan-400 hover:bg-slate-50'}
        `}
        onDrop={(e) => { setIsDragging(false); onDrop(e); }}
        onDragOver={(e) => { setIsDragging(true); onDragOver(e); }}
        onDragLeave={() => setIsDragging(false)}
        onClick={() => fileInputRef.current?.click()}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept=".log,.txt,.csv,.json"
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) onFileSelect(file);
          }}
        />

        <div className="w-16 h-16 mx-auto mb-6 bg-gradient-to-br from-cyan-100 to-blue-100 rounded-2xl flex items-center justify-center">
          <Upload className={`w-8 h-8 ${isDragging ? 'text-cyan-600' : 'text-slate-500'}`} />
        </div>

        <h2 className="text-xl font-semibold text-slate-800 mb-2">
          Drop VPC Flow Logs here
        </h2>
        <p className="text-slate-500 mb-6">
          or click to browse your files
        </p>

        <div className="flex items-center justify-center gap-2 text-sm text-slate-400">
          <FileText className="w-4 h-4" />
          <span>Supported: .json, .log, .txt, .csv (VPC Flow Logs)</span>
        </div>

        <div className="mt-8 p-4 bg-slate-50 rounded-xl text-left space-y-3">
          <div>
            <p className="text-xs font-semibold text-slate-600 mb-1">JSON (CloudWatch export):</p>
            <code className="text-[10px] text-slate-500 font-mono leading-relaxed block overflow-x-auto">
              {`{"events": [{"message": "2 808953420806 eni-xxx 10.1.1.1 10.2.2.2 443 42438 6 30 6651 ... ACCEPT OK"}]}`}
            </code>
          </div>
          <div>
            <p className="text-xs font-semibold text-slate-600 mb-1">Plain text:</p>
            <code className="text-[10px] text-slate-500 font-mono leading-relaxed block overflow-x-auto">
              2 808953420806 eni-xxx 10.1.1.1 10.2.2.2 443 42438 6 30 6651 1768948062 1768948062 ACCEPT OK
            </code>
          </div>
        </div>
      </div>
    </div>
  );
};

// =============================================================================
// STREAM LIST VIEW
// =============================================================================

interface StreamListViewProps {
  streams: FlowStream[];
  selectedStreams: Set<string>;
  filter: StreamFilter;
  stats: ParseStats | null;
  onFilterChange: (filter: StreamFilter) => void;
  onStreamSelect: (id: string, selected: boolean) => void;
  onSelectAll: () => void;
  onDeselectAll: () => void;
  onVisualize: () => void;
}

const ROWS_PER_PAGE = 100;

const StreamListView: React.FC<StreamListViewProps> = ({
  streams,
  selectedStreams,
  filter,
  stats,
  onFilterChange,
  onStreamSelect,
  onSelectAll,
  onDeselectAll,
  onVisualize,
}) => {
  const [currentPage, setCurrentPage] = useState(0);
  
  // Reset page when streams change
  useEffect(() => {
    setCurrentPage(0);
  }, [streams.length]);
  
  // Calculate pagination
  const totalPages = Math.ceil(streams.length / ROWS_PER_PAGE);
  const startIndex = currentPage * ROWS_PER_PAGE;
  const endIndex = Math.min(startIndex + ROWS_PER_PAGE, streams.length);
  const visibleStreams = streams.slice(startIndex, endIndex);
  
  console.log('[StreamListView] Rendering page', currentPage + 1, 'of', totalPages, '- showing', visibleStreams.length, 'of', streams.length, 'streams');
  
  return (
    <div className="h-full flex flex-col bg-slate-50">
      {/* Stats Banner */}
      {stats && (
        <div className="bg-white border-b border-slate-200 px-6 py-3">
          <div className="flex items-center gap-8 text-sm">
            <div>
              <span className="text-slate-500">Unique Streams:</span>{' '}
              <span className="font-bold text-slate-800">{formatNumber(stats.uniqueStreams)}</span>
            </div>
            <div>
              <span className="text-slate-500">Valid Records:</span>{' '}
              <span className="font-bold text-slate-800">{formatNumber(stats.validRecords)}</span>
            </div>
            <div>
              <span className="text-slate-500">Accepted:</span>{' '}
              <span className="font-bold text-emerald-600">{formatNumber(stats.acceptCount)}</span>
            </div>
            <div>
              <span className="text-slate-500">Rejected:</span>{' '}
              <span className="font-bold text-red-600">{formatNumber(stats.rejectCount)}</span>
            </div>
            {stats.skippedRecords > 0 && (
              <div>
                <span className="text-slate-500">Skipped:</span>{' '}
                <span className="font-medium text-slate-400">{formatNumber(stats.skippedRecords)}</span>
              </div>
            )}
          </div>
        </div>
      )}
      
      {/* Filters */}
      <div className="bg-white border-b border-slate-200 px-6 py-3 flex items-center gap-4 flex-shrink-0">
        {/* Protocol Filter */}
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-slate-400" />
          <select
            value={filter.protocol}
            onChange={(e) => onFilterChange({ ...filter, protocol: e.target.value as any })}
            className="text-sm border border-slate-200 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-cyan-500"
          >
            <option value="all">All Protocols</option>
            <option value="tcp">TCP</option>
            <option value="udp">UDP</option>
            <option value="icmp">ICMP</option>
          </select>
        </div>

        {/* Action Filter */}
        <select
          value={filter.action}
          onChange={(e) => onFilterChange({ ...filter, action: e.target.value as any })}
          className="text-sm border border-slate-200 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-cyan-500"
        >
          <option value="all">All Actions</option>
          <option value="ACCEPT">ACCEPT</option>
          <option value="REJECT">REJECT</option>
        </select>

        {/* Direction Filter */}
        <select
          value={filter.direction}
          onChange={(e) => onFilterChange({ ...filter, direction: e.target.value as any })}
          className="text-sm border border-slate-200 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-cyan-500"
        >
          <option value="all">All Directions</option>
          <option value="inbound">Inbound</option>
          <option value="outbound">Outbound</option>
        </select>

        {/* IP Search */}
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="text"
            value={filter.searchIp}
            onChange={(e) => onFilterChange({ ...filter, searchIp: e.target.value })}
            placeholder="Search IP address..."
            className="w-full text-sm border border-slate-200 rounded-lg pl-9 pr-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-cyan-500"
          />
        </div>

        <div className="flex-1" />

        {/* Selection controls */}
        <div className="flex items-center gap-2 text-sm">
          <button onClick={onSelectAll} className="text-cyan-600 hover:text-cyan-700">
            Select all
          </button>
          <span className="text-slate-300">|</span>
          <button onClick={onDeselectAll} className="text-slate-500 hover:text-slate-600">
            Deselect all
          </button>
        </div>
      </div>

      {/* Stream Table */}
      <div className="flex-1 overflow-auto p-6">
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-4 py-3 text-left w-12">
                  <input
                    type="checkbox"
                    checked={selectedStreams.size === streams.length && streams.length > 0}
                    onChange={(e) => e.target.checked ? onSelectAll() : onDeselectAll()}
                    className="rounded border-slate-300"
                  />
                </th>
                <th className="px-4 py-3 text-left font-semibold text-slate-600">Direction</th>
                <th className="px-4 py-3 text-left font-semibold text-slate-600">Type</th>
                <th className="px-4 py-3 text-left font-semibold text-slate-600">Source</th>
                <th className="px-4 py-3 text-left font-semibold text-slate-600">Destination</th>
                <th className="px-4 py-3 text-left font-semibold text-slate-600">Src Port</th>
                <th className="px-4 py-3 text-left font-semibold text-slate-600">Dst Port</th>
                <th className="px-4 py-3 text-left font-semibold text-slate-600">Protocol</th>
                <th className="px-4 py-3 text-left font-semibold text-slate-600">Action</th>
                <th className="px-4 py-3 text-right font-semibold text-slate-600">Packets</th>
                <th className="px-4 py-3 text-right font-semibold text-slate-600">Bytes</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {visibleStreams.map((stream) => (
                <tr
                  key={stream.id}
                  className={`hover:bg-slate-50 cursor-pointer transition-colors ${
                    selectedStreams.has(stream.id) ? 'bg-cyan-50' : ''
                  }`}
                  onClick={() => onStreamSelect(stream.id, !selectedStreams.has(stream.id))}
                >
                  <td className="px-4 py-3">
                    <input
                      type="checkbox"
                      checked={selectedStreams.has(stream.id)}
                      onChange={(e) => {
                        e.stopPropagation();
                        onStreamSelect(stream.id, e.target.checked);
                      }}
                      className="rounded border-slate-300"
                    />
                  </td>
                  <td className="px-4 py-3">
                    <DirectionBadge direction={stream.direction === 'unknown' ? 'inbound' : stream.direction} />
                  </td>
                  <td className="px-4 py-3">
                    <ConnectionTypeBadge 
                      type={stream.connectionType} 
                      isStatefulAllowed={stream.isStatefulAllowed}
                      hasRelated={!!stream.relatedStreamId}
                    />
                  </td>
                  <td className="px-4 py-3 font-mono text-slate-700">{stream.srcAddr}</td>
                  <td className="px-4 py-3 font-mono text-slate-700">{stream.dstAddr}</td>
                  <td className="px-4 py-3 font-mono text-slate-600">{stream.srcPort}</td>
                  <td className="px-4 py-3 font-mono text-slate-600">{stream.dstPort}</td>
                  <td className="px-4 py-3">
                    <span className="px-2 py-0.5 bg-slate-100 rounded text-slate-600 text-xs font-medium">
                      {stream.protocolName}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={`
                        inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold
                        ${stream.action === 'ACCEPT'
                          ? 'bg-emerald-100 text-emerald-700'
                          : 'bg-red-100 text-red-700'}
                      `}
                    >
                      {stream.action === 'ACCEPT' ? (
                        <CheckCircle2 className="w-3 h-3" />
                      ) : (
                        <XCircle className="w-3 h-3" />
                      )}
                      {stream.action}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right font-mono text-slate-600">
                    {formatNumber(stream.totalPackets)}
                  </td>
                  <td className="px-4 py-3 text-right font-mono text-slate-600">
                    {formatBytes(stream.totalBytes)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {streams.length === 0 && (
            <div className="p-12 text-center text-slate-500">
              No streams match the current filters
            </div>
          )}
        </div>
      </div>

      {/* Footer */}
      <div className="bg-white border-t border-slate-200 px-6 py-4 flex items-center justify-between flex-shrink-0">
        <div className="text-sm text-slate-500">
          Selected: <span className="font-semibold text-slate-700">{selectedStreams.size}</span> of {streams.length} streams
        </div>
        
        {/* Pagination Controls */}
        {totalPages > 1 && (
          <div className="flex items-center gap-2">
            <button
              onClick={() => setCurrentPage(0)}
              disabled={currentPage === 0}
              className="px-2 py-1 text-sm border border-slate-200 rounded hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              First
            </button>
            <button
              onClick={() => setCurrentPage(p => Math.max(0, p - 1))}
              disabled={currentPage === 0}
              className="px-3 py-1 text-sm border border-slate-200 rounded hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Prev
            </button>
            <span className="px-3 py-1 text-sm text-slate-600">
              Page {currentPage + 1} of {totalPages}
            </span>
            <button
              onClick={() => setCurrentPage(p => Math.min(totalPages - 1, p + 1))}
              disabled={currentPage >= totalPages - 1}
              className="px-3 py-1 text-sm border border-slate-200 rounded hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
            <button
              onClick={() => setCurrentPage(totalPages - 1)}
              disabled={currentPage >= totalPages - 1}
              className="px-2 py-1 text-sm border border-slate-200 rounded hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Last
            </button>
            <span className="text-sm text-slate-400 ml-2">
              (Showing {startIndex + 1}-{endIndex} of {streams.length})
            </span>
          </div>
        )}
        
        <button
          onClick={onVisualize}
          disabled={selectedStreams.size === 0}
          className={`
            flex items-center gap-2 px-4 py-2 rounded-lg font-semibold text-sm transition-all
            ${selectedStreams.size > 0
              ? 'bg-gradient-to-r from-cyan-500 to-blue-600 text-white hover:from-cyan-600 hover:to-blue-700 shadow-lg shadow-cyan-500/25'
              : 'bg-slate-100 text-slate-400 cursor-not-allowed'}
          `}
        >
          <Zap className="w-4 h-4" />
          Visualize Selected Flows
        </button>
      </div>
    </div>
  );
};

// =============================================================================
// VISUALIZATION VIEW
// =============================================================================

interface VisualizationViewProps {
  results: TrafficPath[];
  nodes: Node[];
  edges: Edge[];
  onNodesChange: any;
  onEdgesChange: any;
}

const VisualizationView: React.FC<VisualizationViewProps> = ({
  results,
  nodes,
  edges,
  onNodesChange,
  onEdgesChange,
}) => {
  const [selectedPath, setSelectedPath] = useState<number>(0);

  return (
    <div className="h-full flex">
      {/* Sidebar */}
      <div className="w-80 bg-white border-r border-slate-200 flex flex-col flex-shrink-0">
        <div className="px-4 py-3 border-b border-slate-200">
          <h3 className="font-semibold text-slate-800">Analyzed Flows</h3>
          <p className="text-xs text-slate-500">{results.length} traffic path{results.length !== 1 ? 's' : ''}</p>
        </div>
        <div className="flex-1 overflow-y-auto">
          {results.map((result, index) => (
            <div
              key={index}
              className={`
                px-4 py-3 border-b border-slate-100 cursor-pointer transition-colors
                ${selectedPath === index ? 'bg-cyan-50 border-l-4 border-l-cyan-500' : 'hover:bg-slate-50'}
              `}
              onClick={() => setSelectedPath(index)}
            >
              <div className="flex items-center gap-2 mb-1">
                <DirectionBadge direction={result.stream.direction === 'unknown' ? 'inbound' : result.stream.direction} />
                <span
                  className={`
                    text-xs px-2 py-0.5 rounded font-semibold
                    ${result.verdict === 'allowed'
                      ? 'bg-emerald-100 text-emerald-700'
                      : 'bg-red-100 text-red-700'}
                  `}
                >
                  {result.verdict.toUpperCase()}
                </span>
              </div>
              <div className="text-xs font-mono text-slate-600">
                {result.stream.srcAddr}:{result.stream.srcPort}
              </div>
              <div className="text-xs text-slate-400">to</div>
              <div className="text-xs font-mono text-slate-600">
                {result.stream.dstAddr}:{result.stream.dstPort}
              </div>
              {result.deniedAt && (
                <div className="mt-2 text-xs text-red-600">
                  Blocked at: {result.deniedAt.name}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* ReactFlow Canvas */}
      <div className="flex-1 h-full">
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          nodeTypes={trafficNodeTypes}
          fitView
          fitViewOptions={{ padding: 0.2 }}
          minZoom={0.1}
          maxZoom={2}
          defaultEdgeOptions={{
            type: 'smoothstep',
            animated: true,
          }}
        >
          <Background color="#94a3b8" gap={20} size={1} />
          <Controls className="bg-white shadow-lg border border-slate-200" />
          <MiniMap
            nodeColor={(node) => {
              const data = node.data as TrafficNodeData;
              if (data.status === 'denied') return '#ef4444';
              if (data.status === 'allowed') return '#10b981';
              return '#94a3b8';
            }}
            className="bg-white shadow-lg border border-slate-200"
          />
        </ReactFlow>
      </div>
    </div>
  );
};

// =============================================================================
// HELPER COMPONENTS
// =============================================================================

const LoadingOverlay: React.FC<{ message?: string }> = ({ message = 'Processing...' }) => (
  <div className="absolute inset-0 bg-white/80 backdrop-blur-sm z-50 flex items-center justify-center">
    <div className="flex flex-col items-center gap-3">
      <div className="w-8 h-8 border-4 border-cyan-500 border-t-transparent rounded-full animate-spin" />
      <span className="text-sm font-medium text-slate-600">{message}</span>
    </div>
  </div>
);

const ErrorBanner: React.FC<{ error: string; onDismiss: () => void }> = ({ error, onDismiss }) => (
  <div className="absolute top-4 left-1/2 -translate-x-1/2 z-50 bg-red-50 border border-red-200 rounded-xl px-4 py-3 flex items-center gap-3 shadow-lg max-w-lg">
    <XCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
    <span className="text-sm text-red-700 flex-1">{error}</span>
    <button onClick={onDismiss} className="p-1 hover:bg-red-100 rounded">
      <X className="w-4 h-4 text-red-500" />
    </button>
  </div>
);

export default TrafficAnalyzer;
